

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";


import {
  Box,
  Tooltip,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Link } from 'react-router-dom';
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip } from "chart.js";
import html2canvas from "html2canvas";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import logo from "../../assets/images/logo.png";
import signup from "../../assets/images/background.png";
import AnimatedScoreRings from "../common/AnimatedScoreRings";
import MetroLineStepper from "../common/MetroLineScore";

import SegmentedBar from "../SegmentedBar";
import SegmentedDoughnut from "../SegmentedDoughnut";
import GradientLayout from "../GradientLayout";
import { useAuth } from "../../auth/AuthContext";
import GlassLoaderOverlay from "../GlassLoader";
import { API_PATH } from "../../environments";
ChartJS.register(ArcElement, ChartTooltip);

const avatarImage = "/path/to/avatar.png";

const domains = ["DevOps", "SRE"];
const navLinks = ["Maturity Assessment", "Contact Us"];


function getUserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getGreeting(timeZone) {
  const now = new Date();
  const userTimeStr = now.toLocaleString("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
  });
  const userHour = parseInt(userTimeStr, 10);

  if (userHour >= 5 && userHour < 12) return "Good Morning!";
  if (userHour >= 12 && userHour < 17) return "Good Afternoon!";
  return "Good Night!";
}

const SpeedometerCard = ({ title, subtitle, score, domain, user = { name: "Guest", email: "guest@example.com" } }) => {
  const size = 160;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = Math.PI * radius;

  const normalizedScore = Math.min(Math.max(score, 0), 100);
  const arcLength = (normalizedScore / 100) * circumference;

  const navigate = useNavigate();

  // Compute pointer angle (180° range)
  const angle = (normalizedScore / 100) * Math.PI;
  const pointerX = center - radius * Math.cos(angle);
  const pointerY = center - radius * Math.sin(angle);


  // Function to determine gradient color based on score
  const getColorForScore = (s) => {
    if (s < 50) return "red";
    if (s < 75) return "#bfa500"; // Dark Yellow
    if (s < 90) return "green"; // New Blue
    return "#2436FF";
  };



  const pointerColor = getColorForScore(score);

  const handlePreviewClick = () => {
    navigate("/recommendation-page", {
      state: {
        category: title,
        score,
        domain,
        name: user.name,
        email: user.email,
      },
    });

  };

  return (
    <Box
      sx={{
        backgroundColor: "rgba(218, 214, 214, 0.9)",
        borderRadius: "25px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        minWidth: 400,
      }}
    >
      {/* Gauge */}
      {/* <Gauge size={160} strokeWidth={12} score={Math.round(score)} /> */}

      {/* Text Right */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, }}>
          {title === "Maturity Score" ? `${title} : ${domain}` : title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "#555", mb: 1, }}>
            {subtitle}
          </Typography>
        )}

        {title === "Maturity Score" ? (
          <Typography
            variant="body2"
            sx={{
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,

            }}
            onClick={() =>
              navigate("/final-page", {
                state: {
                  name: user.name,
                  email: user.email,
                  domain,
                },
              })
            }
          >
            To know more... Contact Us
          </Typography>
        ) : (

          <Typography
            variant="body2"
            sx={{
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,

            }}
            onClick={handlePreviewClick}
          >
            Preview Detailed Recommendations
          </Typography>
        )}

      </Box>
    </Box>
  );
};


const generateDonutChartBase64 = (score) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 300;

    const ctx = canvas.getContext("2d");

    const chart = new ChartJS(ctx, {
      type: "doughnut",
      data: {
        labels: ["Score", "Remaining"],
        datasets: [
          {
            data: [score, 100 - score],
            backgroundColor: ["#0F62FE", "#E0E0E0"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: false,
        animation: {
          duration: 0, // IMPORTANT → makes PNG stable
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        cutout: "70%",
      },
    });

    // 🔥 Give Chart.js time to finish internal rendering
    setTimeout(() => {
      const img = canvas.toDataURL("image/png", 1.0);

      chart.destroy(); // cleanup
      resolve(img);
    }, 150); // 150ms ensures PNG is valid
  });
};

const getRecommendationColors = (score) => {
  if (score <= 50)
    return { bg: "#FDECEA", text: "#C71313" };
  if (score <= 75)
    return { bg: "#FFF7E0", text: "#FBBC05" };
  if (score <= 90)
    return { bg: "#EAF7EA", text: "#008200" };
  return { bg: "#EEF3FF", text: "#0F62FE" };
};
const STEP_COLORS = ["#babdc1", "#d32f2f", "#fbc02d", "#2e7d32", "#1976d2"];
const steps = ["    ", "Initial", "Managed", "Measured", "Optimized"];
export default function SreScores() {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentDomain, setCurrentDomain] = useState("SRE");
  const [timeZone, setTimeZone] = useState("UTC");
  const [greeting, setGreeting] = useState("Hello!");
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // New state for fetched scores
  const [categoryScores, setCategoryScores] = useState(null);
  const [totalScore, setTotalScore] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState([]);

  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [evaluationId, setEvaluationId] = useState(null);
  const [userDomain, setUserDomain] = useState("");
  const [evaluationDateTime, setEvaluationDateTime] = useState(null);
  const params = new URLSearchParams(location.search);
  const createdAtParam = params.get("createdAt");
  const getMaturityLevel = (score) => {
    if (score === null || score === undefined) return "--";
    if (score >= 0 && score <= 50) return "Initial";
    if (score >= 51 && score <= 75) return "Managed";
    if (score >= 76 && score <= 89) return "Measured";
    if (score >= 90 && score <= 100) return "Optimized";
    return "--";
  };
  const totalMaturityLevel =
    totalScore?.averageScore !== undefined
      ? getMaturityLevel(Math.round(totalScore.averageScore))
      : "--";
  const maxScore = 100;
  const stepScore = maxScore / 4;
  const completedSteps=steps.indexOf(totalMaturityLevel);
  // const completedSteps = totalMaturityLevel ==="Optimized"?Math.floor(
  //   Math.round(totalScore?.averageScore || 0) / stepScore)+1:Math.floor(
  //   Math.round(totalScore?.averageScore || 0) / stepScore,
  // );
  useEffect(() => {
    if (createdAtParam) {
      setEvaluationDateTime(createdAtParam);
    }
  }, [createdAtParam]);


  useEffect(() => {
    if (location.state?.createdAt) {
      setEvaluationDateTime(location.state.createdAt);
    }
  }, [location.state]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const domainParam = params.get("domain");
    const emailParam = params.get("email");
    const nameParam = params.get("name");
    const evaluationIdParam = params.get("evaluationId");

    if (emailParam) {
      setUserEmail(decodeURIComponent(emailParam));
    }
    if (nameParam) {
      setUserName(decodeURIComponent(nameParam));
    }

    if (evaluationIdParam) setEvaluationId(evaluationIdParam);
    if (domainParam) {
      const normalizedDomain = domains.find(
        (d) => d.toLowerCase() === domainParam.toLowerCase()
      );
      setCurrentDomain(normalizedDomain || "SRE");
    } else {
      setCurrentDomain("SRE");
    }
  }, [location.search]);


  useEffect(() => {
    const tz = getUserTimeZone();
    setTimeZone(tz);
    setGreeting(getGreeting(tz));
  }, []);




  //newly added
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const evaluationIdParam = params.get("evaluationId");

    // ✅ Skip domain-based fetch entirely if evaluationId exists
    if (evaluationIdParam) return;

    if (!userEmail) return;

    const fetchScores = async () => {
      setLoading(true);
      setError(null);
      try {
        // http://localhost:8085/api/evaluations/users/${userEmail}/category-raw-scores?domain=${currentDomain}
        const categoryRes = await fetch(
          `${API_PATH}/evaluations/users/${userEmail}/category-raw-scores?domain=${currentDomain}`,{credentials:'include'}
        );
        if (!categoryRes.ok) throw new Error(`Category API error: ${categoryRes.status}`);
        const categoryData = await categoryRes.json();
          // `http://localhost:8085/api/evaluations/user/${encodeURIComponent
        const totalRes = await fetch(
          `${API_PATH}/evaluations/user/${encodeURIComponent(
            userEmail
          )}/domain/${currentDomain}/score`,{credentials:'include'}
        );
        if (!totalRes.ok) throw new Error(`Total score API error: ${totalRes.status}`);
        const totalData = await totalRes.json();

        if (categoryData[categoryData.length - 1]?.domain === currentDomain) {
          setCategoryScores(categoryData[categoryData.length - 1].categoryScores);
        } else {
          setCategoryScores(null);
        }

        setTotalScore(totalData);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
        setCategoryScores(null);
        setTotalScore(null);
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [currentDomain, userEmail, location.search]); // ✅ add location.search

  const [animationProgress, setAnimationProgress] = useState(0);
  useEffect(() => {
    let start = 0;
    const target = 100;
    const step = () => {
      start += 2; // speed of animation
      setAnimationProgress(start);
      if (start < target) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, []);


  useEffect(() => {
    if (!userEmail || !currentDomain) return;

    const params = new URLSearchParams(location.search);
    const evaluationIdParam = params.get("evaluationId");

    const fetchScores = async () => {
      setLoading(true);
      setError(null);

      try {
        // ✅ Fetch category-level scores (same as before)
        // http://localhost:8085/api/evaluations/users/${encodeURIComponent
        const categoryUrl = evaluationIdParam
          ?`${API_PATH}/evaluations/users/${encodeURIComponent(
            userEmail
          )}/category-raw-scores?evaluationId=${evaluationIdParam}`
          // http://localhost:8085/api/evaluations/users/${encodeURIComponent
          : `${API_PATH}/evaluations/users/${encodeURIComponent(
            userEmail
          )}/category-raw-scores?domain=${currentDomain}`;

        const categoryRes = await fetch(categoryUrl,{credentials:'include'});
        if (!categoryRes.ok) throw new Error(`Category API error: ${categoryRes.status}`);

        const categoryData = await categoryRes.json();
        if (Array.isArray(categoryData) && categoryData.length > 0) {
          //const evaluation = categoryData[0];
          const evaluation = categoryData[categoryData.length - 1];
          setCategoryScores(evaluation.categoryScores || {});
          setUserDomain(evaluation.userDomain || "");
          setEvaluationDateTime((prev) => prev || evaluation?.createdAt || null);
        } else {
          setCategoryScores(null);
          setUserDomain("");
        }

        // ✅ Fetch total score (new unified API)
        // http://localhost:8085/api/evaluations/user/${encodeURIComponent
        const totalUrl = evaluationIdParam
          ? `${API_PATH}/evaluations/user/${encodeURIComponent(
            userEmail
          )}/domain/${currentDomain}/score?evaluationId=${evaluationIdParam}`
          // http://localhost:8085/api/evaluations/user/${encodeURIComponent
          : `${API_PATH}/evaluations/user/${encodeURIComponent(
            userEmail
          )}/domain/${currentDomain}/score`;

        const totalRes = await fetch(totalUrl,{credentials:'include'});
        if (!totalRes.ok) throw new Error(`Total Score API error: ${totalRes.status}`);

        const totalData = await totalRes.json();
        setTotalScore(totalData);

      } catch (err) {
        console.error("Error fetching scores:", err);
        setError(err.message);
        setCategoryScores(null);
        setTotalScore(null);
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [currentDomain, userEmail, location.search]);

  const handleDomainClick = (domain) => {
    const path = domain === "SRE" ? "/sre-questions" : "/devops-questions";
    navigate(path, {
      state: {
        name: userName,
        email: userEmail,
        domain,
      },
    });
  };


  const handleNavLinkClick = (e, idx) => {
    const appBarTitle = e.target.innerText;
    if (appBarTitle === "Maturity Assessment") {
      navigate(`/sre-score?domain=${encodeURIComponent(currentDomain)}&email=${encodeURIComponent(userEmail)}&name=${encodeURIComponent(user.name)}/`);
    }
    else {
      navigate(`/contactus-page`)
    }
  }

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };


  const user = {
    name: userName,
    email: userEmail,
  };

  const handleLogout = () => {
    handleUserMenuClose();
    navigate("/login");
  };


  const getBase64ImageFromUrl = async (imageUrl) => {
    const response = await fetch(imageUrl,{credentials:'include'});
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };



  const getDonutColor = (score) => {
    if (score <= 50) return "#C71313";
    if (score <= 75) return "#FBBC05";
    if (score <= 90) return "#008200";
    return "#0F62FE";
  };

  const captureAnimatedRing = async () => {
    const element = document.querySelector("#score-pdf-ring-wrapper .score-pdf-ring-area");

    if (!element) {
      console.error("Ring wrapper not found");
      return null;
    }

    const canvas = await html2canvas(element, {
      backgroundColor: null,
      scale: 3,
      useCORS: true,
    });

    return canvas.toDataURL("image/png", 1.0);
  };

  const getImageSize = (base64) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = base64;
    });
  };


  const handleDownloadReport = async () => {
    let modules = ["Excellent", "Good", "Needs Improvement"];
    const evaluationIdParam = params.get("evaluationId");
    const domain = currentDomain;

    try {
      const logoBase64 = await getBase64ImageFromUrl(logo);
// http://localhost:8085/api/evaluations/user/${encodeURIComponent
      const response = await fetch(
        `${API_PATH}/evaluations/user/${encodeURIComponent(
          user.email
        )}/domain/${domain}`,{credentials:'include'}
      );

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const data = await response.json();
      // const answers = data[0]?.answers || [];
      const answers = data?.filter((item) => item?.evaluationId == evaluationIdParam);

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      // ⭐ Capture the real animated ring
      const chartImage = await captureAnimatedRing();

      // ⭐ Detect actual width/height of PNG to prevent oval distortion
      const { width: imgW, height: imgH } = await getImageSize(chartImage);

      // Use minimum dimension to keep perfect circle
      const ringSizePx = Math.min(imgW, imgH);

      // PDF size for ring
      const renderSize = 20; // 40mm

      // Scale proportionally
      const scale = renderSize / ringSizePx;

      const renderWidth = imgW * scale;
      const renderHeight = imgH * scale;

      // Position
      const donutX = 15; // reduced from 150 to avoid overflow

      const donutSize = 45; // slightly larger ring but stays inside A4


      // Add image without distortion
      // doc.addImage(chartImage, "PNG", donutX, donutY, renderWidth, renderHeight);
        doc.addImage(logoBase64, "PNG", 14, 8, 40, 10);



      // ---------- HEADER ----------
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 54, 221);
      doc.setFontSize(16);
      doc.text(`Devops and SRE Maturity Compass `, 100, 18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      // eslint-disable-next-line eqeqeq
      doc.text(
        `${currentDomain} Maturity Assessment`,
        currentDomain == "SRE" ? 135 : 125,
        28,
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);

      if (totalScore?.averageScore !== undefined) {
        const finalScore = Math.round(totalScore.averageScore);
        const maturityLevel = getMaturityLevel(finalScore);

        doc.setFillColor(230, 235, 245);
        doc.roundedRect(15, 30, 180, 40, 2, 2, "F");

        doc.setFontSize(12);
        doc.setTextColor(80);

        doc.text("Domain:", 17, 37);
        doc.setFont(undefined, "bold");
        doc.text(userDomain || "--", 37, 37);
        doc.setFont(undefined, "normal");
        doc.text("|", 67, 37);
        doc.text("Maturity Score:", 70, 37);
        doc.setFont(undefined, "bold");
        doc.text(`${finalScore}%`, 100, 37);
        doc.setFont(undefined, "normal");
        doc.text("|", 115, 37);
        doc.text("Maturity Level:", 117, 37);
        doc.setFont(undefined, "bold");
        doc.text(maturityLevel, 150, 37);
      }

      // ---------- MATURITY LEVEL BOXES ----------
      // doc.setFont("helvetica", "bold");
      // doc.setFontSize(10);
      // doc.text("Maturity Levels:", 14, 70);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(20, 45, 170, 22, 2, 2, "F");
      doc.setTextColor(0, 0, 0);
      doc.addImage(chartImage, "PNG", donutX, 47, 120, 10);
      doc.setFontSize(10);
      doc.setFont("helvetica", completedSteps === 1 ? "bold" : "normal");
      doc.text("Initial", 47, 64);
      doc.setFont("helvetica", completedSteps === 2 ? "bold" : "normal");
      doc.text("Managed", 65, 64);
      doc.setFont("helvetica", completedSteps === 3 ? "bold" : "normal");
      doc.text("Measured", 90, 64);
      doc.setFont("helvetica", completedSteps === 4 ? "bold" : "normal");
      doc.text("Optimized", 115, 64);

      // const tableStartY = boxY + boxHeight + 6;

      // ---------- NORMALIZATION ----------
      const normalize = (remark) => {
        if (!remark) return "";
        const r = remark.toLowerCase();
        if (r.includes("excellent")) return "Excellent";
        if (r.includes("good")) return "Good";
        // if (r.includes("basic")) return "Basic";
        if (r.includes("needs")) return "Needs Improvement";
        return "";
      };

      const formatRecs = (items, remarkType) => {
        return (
          [
            ...new Set(
              items
                .filter((i) => normalize(i.remarks) === remarkType)
                .map((i) => i.recommendation?.trim())
                .filter(Boolean),
            ),
          ]
            .map((t) => `• ${t}`)
            .join("\n") || "-"
        );
      };

      // ---------- TABLE DATA ----------
      const tableData = [];
      const categories = categoryScores ? Object.keys(categoryScores) : [];

      categories.forEach((cat) => {
        const items = answers[0]?.answers?.filter((i) => i.category === cat);

        const allRecs = [
          ...new Set(items.map((i) => i.recommendation?.trim()).filter(Boolean)),
        ];

        const limited = items.filter((i) =>
          allRecs.slice(0, 5).includes(i.recommendation?.trim())
        );

        tableData.push([
          cat,
          formatRecs(limited, "Excellent"),
          formatRecs(limited, "Good"),
          // formatRecs(limited, "Basic"),
          formatRecs(limited, "Needs Improvement"),
        ]);
      });
      /////////////////////////////////////////////////////////////////////////////////////////////
      doc.setFillColor(230, 235, 245);
      // doc.setFontSize(12);
      // doc.roundedRect(15, 73, 25, 7, 1, 1, "F");
      // doc.setFont("Akkurat", "Normal");
      // doc.text("Module", 20, 78);
      // doc.setFont("helvetica", "normal");
      // let y = 88;

      /////////////////////////////////////////////////////////////////////////////////////////////

let y = 75;

const descBoxes = [
  {
    title: "Initial",
    color: [199, 19, 19],
    text: "The environment lacks alignment with best practices, \r\nresulting in manual bottlenecks and high operational risk.",
  },
  {
    title: "Managed",
    color: [251, 188, 5],
    text: "Core best practices are present, but execution is inconsistent and \r\nrequires structural maturity to scale.",
  },
  {
    title: "Measured",
    color: [0, 130, 0],
    text: "Industry best practices are integrated with robust tooling and \r\ntelemetry, enabling objective performance tracking.",
  },
  {
    title: "Optimized",
    color: [15, 98, 254],
    text: "The system operates with peak efficiency, leveraging advanced \r\nmethodologies and continuous security compliance.",
  },
];

let topBoxX = 14;
let topBoxY = y;
const topBoxWidth = 90;
const topBoxHeight = 14;

descBoxes.forEach((b, index) => {
  if (index === 2) {
    topBoxX = 14;
    topBoxY += topBoxHeight + 4;
  }

  doc.setDrawColor(255, 255, 255);
  doc.rect(topBoxX, topBoxY, topBoxWidth, topBoxHeight);

  doc.setFillColor(245, 246, 248);
  doc.rect(topBoxX, topBoxY, 20, 5, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...b.color);
  doc.text(b.title, topBoxX + 2, topBoxY + 3.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(b.text, topBoxX + 2, topBoxY + 9);

  topBoxX += topBoxWidth + 2;
});

// ✅ IMPORTANT
// move y below desc boxes
y = topBoxY + topBoxHeight + 12;

/////////////////////////////////////////////////////////////////////////////////////////////
// MODULE HEADER

doc.setFillColor(230, 235, 245);
doc.setFontSize(12);
doc.roundedRect(15, y - 2, 25, 7, 1, 1, "F");

doc.setFont("helvetica", "bold");
doc.setTextColor(0, 0, 0);
doc.text("Module", 20, y + 3);

doc.setFont("helvetica", "normal");

// table starts AFTER module title
y += 12;

      const pageHeight = doc.internal.pageSize.getHeight();

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const usableWidth = pageWidth - margin * 2;
      const lineHeight = 7;
      console.log("tableData=>", tableData);
      let showmodule = true;

      tableData.map((paragraphs, index) => {
        const count = paragraphs.filter(item => item === '-').length;
        if(count<=2)
        paragraphs.map((p, index) => {
          const lines = doc.splitTextToSize(p, usableWidth);
          showmodule = true;

          lines.map((line, i) => {
            if (index == 0) {
              doc.setFillColor(230, 235, 245);
              doc.setFontSize(12);

              doc.roundedRect(15, y - 5, 180, 7, 1, 1, "F");
              doc.setFont("helvetica", "bold");
              doc.text(line, margin, y);
              doc.setFont("helvetica", "normal");
              y += lineHeight;
              // eslint-disable-next-line no-const-assign
              // showmodule = true;
            } else {
              if (showmodule) {
                doc.setFontSize(12);

                doc.setFont("helvetica", "bold");
                doc.text(modules[index - 1], margin, y);
                doc.setFont("helvetica", "normal");
                y += lineHeight;
                showmodule = false;
              }

              // if (y + lineHeight > pageHeight - margin) {
              //   doc.addPage();
              //   y = margin;
              // }
              const footerHeight = 12;
              if (y + lineHeight > pageHeight - margin - footerHeight) {
                doc.addPage();
                y = margin;


              }
              doc.setFontSize(10);

              doc.text(line !== "-" ? line : "• NA", margin, y);
              y += lineHeight;
 
            }
          });
          y += 5;
        });
          doc.setFontSize(9);
          doc.setTextColor(119, 128, 136)
          doc.text("Generated by Sandhata Technologies", 14, pageHeight - 10);

          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const dateString = new Date().toLocaleString("en-IN", {
            timeZone: tz,
            dateStyle: "medium",
            timeStyle: "short",
          });

          doc.text(
            `Generated on: ${dateString} (${tz})`,
            pageWidth - 14,
            pageHeight - 10,
            { align: "right" }
          );
          doc.setTextColor(0,0,0)
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
          );
             
      });

      /////////////////////////////////////////////////////////////////////////////////////////////

      // const descBoxes = [
      //   {
      //     title: "Initial",
      //     color: [199, 19, 19],
      //     text: "The environment lacks alignment with DevSecOps best practices, \r\nresulting in manual bottlenecks and high operational risk.",
      //   },
      //   {
      //     title: "Managed",
      //     color: [251, 188, 5],
      //     text: "Core best practices are present, but execution is inconsistent and \r\nrequires structural maturity to scale.",
      //   },
      //   {
      //     title: "Measured",
      //     color: [0, 130, 0],
      //     text: "Industry best practices are integrated with robust tooling and \r\ntelemetry, enabling objective performance tracking.",
      //   },
      //   {
      //     title: "Optimize",
      //     color: [15, 98, 254],
      //     text: "The system operates with peak efficiency, leveraging advanced \r\nDevOps methodologies and continuous security compliance.",
      //   },
      // ];

      // let boxX = 14;
      // let boxY = y + 10;
      // const boxWidth = 90;
      // const boxHeight = 14;
      // if((boxY+100) >275){
      //   boxY=20
      //   doc.addPage()
      //    doc.setFontSize(9);
      //     doc.setTextColor(119, 128, 136)
      //     doc.text("Generated by Sandhata Technologies", 14, pageHeight - 10);

      //     const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //     const dateString = new Date().toLocaleString("en-IN", {
      //       timeZone: tz,
      //       dateStyle: "medium",
      //       timeStyle: "short",
      //     });

      //     doc.text(
      //       `Generated on: ${dateString} (${tz})`,
      //       pageWidth - 14,
      //       pageHeight - 10,
      //       { align: "right" }
      //     );
      //     doc.setTextColor(0,0,0)
      //     doc.text(
      //       `Page ${doc.internal.getNumberOfPages()}`,
      //       pageWidth / 2,
      //       pageHeight - 10,
      //       { align: "center" }
      //     );
      // }

      // descBoxes.forEach((b, index) => {
      //   if (index === 2) {
      //     boxX = 14;
      //     boxY += boxHeight + 3;
      //   }

      //   doc.setDrawColor(255,255,255);
      //   doc.rect(boxX, boxY, boxWidth, boxHeight);

      //   doc.setFillColor(245, 246, 248);
      //   doc.rect(boxX, boxY, 20, 5, "F");

      //   doc.setFont("helvetica", "normal");
      //   doc.setFontSize(9);
      //   doc.setTextColor(...b.color);
      //   doc.text(b.title, boxX + 2, boxY + 3.5);

      //   doc.setFont("helvetica", "normal");
      //   doc.setFontSize(8);
      //   doc.setTextColor(0, 0, 0);
      //   doc.text(b.text, boxX + 2, boxY + 9);

      //   boxX += boxWidth + 2;
      // });

      /////////////////////////////////////////////////////////////////////////////////////////////////

      // ---------- TABLE SECTION ----------
      // autoTable(doc, {
      //   startY: tableStartY,
      //   margin: { top: 20 },
      //   head: [["Module", "Excellent", "Good", "Needs Improvement"]],
      //   body: tableData,

      //   styles: {
      //     fontSize: 10,
      //     cellPadding: 2,
      //     lineColor: [220, 220, 220],
      //     lineWidth: 0.1,
      //   },

      //   didDrawPage: () => {
      //     const pageHeight = doc.internal.pageSize.getHeight();
      //     const pageWidth = doc.internal.pageSize.getWidth();

      //     doc.addImage(logoBase64, "PNG", 14, 8, 35, 8);

      //     doc.setFontSize(9);
      //     doc.text("Generated by Sandhata Technologies", 14, pageHeight - 10);

      //     const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //     const dateString = new Date().toLocaleString("en-IN", {
      //       timeZone: tz,
      //       dateStyle: "medium",
      //       timeStyle: "short",
      //     });

      //     doc.text(
      //       `Generated on: ${dateString} (${tz})`,
      //       pageWidth - 14,
      //       pageHeight - 10,
      //       { align: "right" }
      //     );

      //     doc.text(
      //       `Page ${doc.internal.getNumberOfPages()}`,
      //       pageWidth / 2,
      //       pageHeight - 10,
      //       { align: "center" }
      //     );
      //   },
      // });

      // ---------- DISCLAIMER ----------
      const disclaimerText =
      "This DevOps and SRE Maturity Report and its associated recommendations are generated automatically based solely on the data "+
      "and responses provided by the participant. Sandhata does not independently verify the accuracy, completeness, or truthfulness of the information submitted. "+
      "The insights provided are for informational and educational purposes only and do not constitute professional consulting, engineering, or legal advice. "+
      "Sandhata accepts no liability for any business decisions, financial losses, or operational outcomes resulting from the use of or reliance upon this report. "+
      "We strongly recommend proper consultation before making any changes."
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      const disclaimerLines = doc.splitTextToSize(disclaimerText, usableWidth);

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text("Disclaimer : ", margin, y);
      y += 5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100, 100, 100);

      // Fit all lines in remaining space above footer — no new page
      const availableSpace = pageHeight - 15 - y;
      const neededSpace = disclaimerLines.length * 5;
      const lineSpacing = neededSpace > availableSpace
        ? Math.max(3.5, availableSpace / disclaimerLines.length)
        : 5;
      disclaimerLines.forEach((line,i ) => {
        doc.text(line, i==0?margin+5:margin, y);
        y += lineSpacing;
      });
      doc.setTextColor(0, 0, 0);
      doc.save(`${currentDomain} Maturity_Assessment.pdf`);
    } catch (err) {
      console.error("Error in downloading report:", err);
      alert("Failed to generate report. Please try again.");
    }
  };
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };



  const getScoreColor = (score) => {
    if (score <= 50) return "#C71313";   // Initial
    if (score <= 75) return "#FBBC05";   // Managed
    if (score <= 90) return "#008200";   // Measured
    return "#0F62FE";                    // Optimize
  };

  // const getScoreBgColor = (score) => {
  //   if (score <= 50) return "#FDECEA";
  //   if (score <= 75) return "#FFF7E0";
  //   if (score <= 90) return "#EAF7EA";
  //   return "#EEF3FF";
  // };




  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.5rem",
        alignSelf: { xs: "flex-start", md: "stretch" },
        padding: 1,
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        height: { xs: "auto", sm: "auto", md: "82vh", lg: "90vh", xl: "88vh" },
        borderRadius: "2.5rem",
        boxShadow: `
          0 0 5px rgba(255,255,255,0.1),
          0 0 5px rgba(255,255,255,0.1),
          0 0 5px rgba(255,255,255,0.05)
        `,
      }}
    >
      {/* Maturity Score Header */}
      <Box
  sx={{
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "3fr 1.5fr" },
    gap: "0.5rem",

    // ✅ APPLY RESPONSIVE SIZE HERE
    width: "100%",
    //alignItems: "stretch",
    mx: "auto", // center on screen
  }}
>
        <Box
          sx={{
            padding: "1rem",
            borderRadius: "1rem",
            border: "1px solid #C0C7CF",
            background: "#FFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto 3fr" },
            columnGap: "1.5rem",
            alignItems: "stretch",
          }}
        >
          {/* ---------- LEFT META COLUMN ---------- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            {/* Badge */}
            <Box
              sx={{
                alignSelf: "flex-start",
                px: "0.75rem",
                py: "0.25rem",
                //borderRadius: "0.5rem",
                fontSize: "0.75rem",
                fontWeight: 400,
                fontFamily:"Akkurat",
                //background: getScoreBgColor(totalScore?.averageScore || 0),
                background: "#F5F6F8",
                color: getScoreColor(totalScore?.averageScore || 0),
              }}
            >
              {getMaturityLevel(Math.round(totalScore?.averageScore || 0))}
            </Box>


            <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
              <strong>Assessment ID:</strong> {evaluationId || "--"}
            </Typography>

            <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
              <strong>Date & Time:</strong>{" "}
              {evaluationDateTime
                ? new Date(evaluationDateTime).toLocaleString("en-IN", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
                : "--"}
            </Typography>
          </Box>

          {/* ---------- VERTICAL DIVIDER ---------- */}
          <Box
            sx={{
              width: "1px",
              backgroundColor: "#E0E0E0",
              display: { xs: "none", md: "block" },
            }}
          />

          {/* ---------- RIGHT CONTENT COLUMN ---------- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 400 }}>
              Total Maturity Score
            </Typography>

            <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>
              Your total maturity level is currently at{" "}
              <strong>{Math.round(totalScore?.averageScore || 0)}%</strong>,
              which is considered <strong>{totalMaturityLevel}</strong>. System
              is leading with optimized process, DevOps methodology and security
              compliance.
            </Typography>

            <Button
              sx={{
                mt: "0.5rem",
                width: "11rem",
                borderRadius: "1.75rem",
                background: "#0036DD",
                color: "#fff",
                textTransform: "none",
                "&:hover": { background: "#002FCC" },
              }}
              onClick={handleDownloadReport}
            >
              Download Report
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            padding: { xs: "0.75rem", md: "1.5rem" },
            borderRadius: "1rem",
            border: "1px solid #C0C7CF",
            background: "#FFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          <div id="ring-only-wrapper" style={{ width: "100%", minWidth: 0 }}>
            <div className="ring-capture-area" style={{ overflowX: "hidden", width: "100%" }}>
               <MetroLineStepper
              activeStep={3}
              score={Math.round(totalScore?.averageScore || 0)}
              maturityLevel={totalMaturityLevel}
              />
            </div>
            <>
              <Box sx={{ mt: 3 }}>
                {true && (
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "2rem",
                        sm: "2.25rem",
                        md: "2.5rem",
                        lg: "2.75rem",
                        xl: "3rem",
                        fontFamily: "Akkurat",
                      },
                      fontWeight: 400,
                      marginLeft:'12px',
                      color: STEP_COLORS[completedSteps],
                      lineHeight: 1,
                      mb: "15px",
                    }}
                  >
                    {Math.round(totalScore?.averageScore || 0)}%
                  </Typography>
                )}
              </Box>
            </>
          </div>
        </Box>
      </Box>




      {/* Scrollable Cards Section */}

      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
          },
          gridAutoFlow: "dense",

          gap: "0.5rem",
          alignSelf: "stretch",
          overflowX: "hidden",
          overflowY: { xs: "visible", md: "auto" },
          height: { xs: "auto", md: "68vh" },
          "&::-webkit-scrollbar": { width: "2px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "white",
            borderRadius: "1rem",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255, 255, 255, 0.7)",
          },
        }}
      >
        {categoryScores &&
          Object.entries(categoryScores).map(([category, details], index) => {
            // 🔁 alternating big/small logic
            const rowIndex = Math.floor(index / 2);
            const isLeft = index % 2 === 0;

            // Row 0 → small | big
            // Row 1 → big | small

            return (
              <Box
                key={category}
                sx={{
                  gridColumn: {
                    xs: "span 1",
                    sm: "span 1",
                  },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "0.625rem",
                  padding: { xs: "1rem", sm: "1.25rem 1rem" },
                  borderRadius: "1rem",
                  border: "1px solid #C0C7CF",
                  background: "#FFF",
                  // minHeight: isBig ? "9rem" : "7.5rem",
                  minHeight:'9rem',
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                    borderColor: "#999",
                  },
                }}
              >
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    px: "0.6rem",
                    py: "0.2rem",
                    //borderRadius: "0.4rem",
                    fontSize: "0.7rem",
                    fontWeight: 400,
                    background: "#F5F6F8",
                    fontFamily:"Akkurat",
                    color: getScoreColor(details.score),
                  }}
                >
                  {getMaturityLevel(details.score)}
                </Box>

                {/* Segmented Bar + Percentage */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    flexWrap: "wrap",
                    mb: "0.25rem",
                  }}
                >
                  <SegmentedBar score={details.score} />

                  <Typography
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      color:
                        details.score <= 50
                          ? "#C71313"
                          : details.score <= 75
                            ? "#FBBC05"
                            : details.score <= 90
                              ? "#008200"
                              : "#0F62FE",
                      fontWeight: 400,
                    }}
                  >
                    {details.score ? `${Math.round(details.score)}%` : "--"}
                  </Typography>
                </Box>

                {/* Text Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    minHeight: "3.5rem",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 400,
                      lineHeight: 1.2,
                    }}
                  >
                    {category}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#000",
                      fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      fontWeight: 400,
                      lineHeight: 1.3,
                    }}
                  >
                    Your {category?.toLowerCase()} is{" "}
                    {Math.round(details?.score || 0)}%{" "}
                    {getMaturityLevel(details?.score)}. Want recommendations?
                  </Typography>
                </Box>

                <Button
                  variant="text"
                  onClick={() => {
                    const maturityLevel = getMaturityLevel(details.score);
                    const { bg, text } = getRecommendationColors(details.score);

                    navigate(
                      `/recommendation-page?evaluationId=${evaluationId}&createdAt=${encodeURIComponent(
                        evaluationDateTime
                      )}`,
                      {
                        state: {
                          category,
                          score: details.score,
                          domain: currentDomain,
                          name: user.name,
                          email: user.email,
                          maturityLevel,
                          recommendationBg: bg,
                          recommendationTextColor: text,
                        },
                      }
                    );
                  }}
                  sx={{
                    alignSelf: "flex-start",
                    color: "#0036DD",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    textDecoration: "underline",
                    textTransform: "none",
                    padding: 0,
                    minWidth: "auto",
                    "&:hover": { color: "#0025A0", background: "transparent" },
                  }}
                >
                  Preview Detailed Recommendations
                </Button>
              </Box>
            );
          })}
      </Box>





      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          mt: 1,
          gap: "1rem", // ✅ space between buttons
          flexWrap: "wrap", // ✅ ensures responsiveness on smaller screens
        }}
      >
      
        <Button
          sx={{
            width: "10rem",
            height: "2.5rem",
            borderRadius: "1.75rem",
            background: "#0036DD",
            color: "#fff",
            fontSize: "0.875rem",
            fontWeight: 400,
            textTransform: "none",
            "&:hover": { background: "#002FCC" },
          }}
          onClick={() => navigate("/history-page?domain=SRE")}
        >
          Back
        </Button>
      </Box>

      <GlassLoaderOverlay open={loading} />

      {/* Hidden MetroLine for PDF capture only */}
      <Box sx={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div id="score-pdf-ring-wrapper" style={{ width: "500px" }}>
          <div className="score-pdf-ring-area" style={{ width: "500px", overflowX: "hidden" }}>
            <MetroLineStepper
              score={Math.round(totalScore?.averageScore || 0)}
              maturityLevel={totalMaturityLevel}
              mt={0}
              showLabels={false}
              connectorWidth="68px"
              circleSize={20}
              circleHeight={12}
            />
          </div>
        </div>
      </Box>
    </Box>
  );
}